import { useLayoutEffect } from "react";
import { toast } from "sonner";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { DynamicFormSchema } from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import { useAdminFormEditorPaths } from "../hooks/use-admin-form-editor-paths";
import {
	rememberFormHydration,
	shouldSkipFormHydration,
} from "../state/entity-type-form-mode-store";
import { useFormEditorStore } from "../state/form-editor-store";
import type { FormRule } from "../types/form-rule-types";

interface FormEditorStoreHydratorProps {
	schema: DynamicFormSchema | undefined;
	/** Form rules parsed from the same envelope as `schema`; defaults to `[]`. */
	formRules?: FormRule[];
	/**
	 * API row identity. A remount with the same key keeps unsaved editor state
	 * so drawer and page can share one draft.
	 */
	hydrationKey?: string | null;
	children: React.ReactNode;
}

/**
 * Resets and hydrates the form editor store on mount. Parent must set `key` to
 * the API row identity (`ticketTypeId:id:version`) so a new version remounts
 * this component, and background refetches with the same identity are no-ops.
 */
export function FormEditorStoreHydrator({
	schema,
	formRules,
	hydrationKey,
	children,
}: FormEditorStoreHydratorProps) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();

	useLayoutEffect(() => {
		if (hydrationKey && shouldSkipFormHydration(hydrationKey)) return;
		if (hydrationKey) rememberFormHydration(hydrationKey);
		const store = useFormEditorStore.getState();
		const result = store.revertToSavedSchema(schema, formRules ?? []);
		if (schema == null) return;
		if (!result.ok) return;

		if (result.warnings.length > 0) {
			toast.message(
				t(paths.formEditor.draftHydrateWarnings, {
					count: result.warnings.length,
				}),
				{
					description: result.warnings.join("\n"),
				},
			);
		}
	}, [hydrationKey, schema, formRules, t, paths]);

	return children;
}
